import type { SealedProduct } from "@hobbyshopify/backend-client";
import { SubtreeController } from "subtree.js/core";
import type { AppDeps } from "../../app-deps";
import {
  addBreaks,
  deleteSpotList,
  loadSpotLists,
  parseSpots,
  saveSpotList,
  uploadLiveCover,
} from "../../core/ops/live-ops";
import { loadSealedProducts } from "../../core/ops/sealed-ops";
import { type BoxDraft, boxRequest, planBox } from "../../core/primitives/box-builder";
import { thrownText } from "../../core/primitives/errors";
import { readImageFile } from "../../core/primitives/image-upload";
import { busyForm, failedForm, failedLoad, idleForm, loaded, loading } from "../../core/primitives/load-status";
import { stripeConnection } from "../../core/primitives/onboarding";
import { SellBoxActions, SellBoxState } from "./sell-box-model";

/** Cents to the dollars string a money field holds. */
function centsToDollars(cents: number): string {
  return (cents / 100).toFixed(2);
}

export type SellBoxDeps = Pick<AppDeps, "client" | "authHandler" | "storeRepository">;

export interface SellBoxRouting {
  /** Back to the queue, where the new breaks now sit. */
  toQueue(): void;
}

/**
 * A box on sale in the shop's queue. No stream is involved: spots go on sale
 * at once and the break waits in the queue until a live cockpit rips it.
 */
export class SellBoxController extends SubtreeController implements SellBoxActions {
  readonly state = new SellBoxState();

  constructor(
    readonly deps: SellBoxDeps,
    readonly routing: SellBoxRouting,
  ) {
    super();
    this.subtree.put(SellBoxState, this.state);
    this.subtree.put(SellBoxActions, this);
    this.sync(() => {
      const store = deps.storeRepository.selectedStore;
      this.state.store.value = store;
      this.state.paymentsReady.value = stripeConnection(store)?.status === "ACTIVE";
    }, [deps.storeRepository]);
    this.sync(() => {
      const shopId = deps.storeRepository.shopId;
      if (shopId === this.#shopId) return;
      this.#shopId = shopId;
      void this.#loadSealed();
      void this.#loadLists();
    }, [deps.storeRepository]);
    this.sync(() => {
      this.state.plan.value = planBox(this.state.draft.value, this.state.templates.value, this.state.pastedSpots.value);
    }, [this.state.draft, this.state.templates, this.state.pastedSpots]);
  }

  #shopId: string | null = null;

  setTitle = (title: string) => this.#patch({ title });
  setCost = (cost: string) => this.#patch({ cost });
  setMarketValue = (marketValue: string) => this.#patch({ marketValue });
  setMultiplier = (multiplier: number) => this.#patch({ multiplier });
  setSpotText = (spotText: string) => this.#patch({ spotText });
  setBreakTime = (breakTime: string) => this.#patch({ breakTime });

  cancel = () => this.routing.toQueue();

  pickSealedProduct = (id: string | null) => {
    const product = id ? (this.state.sealedProducts.value.find((p) => p.id === id) ?? null) : null;
    if (!product) {
      this.#patch({ sealedProductId: null });
      return;
    }
    // The league list that matches the product's sport, when there is one; a pasted list is left alone.
    const template = this.state.templates.value.find((t) => t.category === product.category) ?? null;
    this.state.draft.update((d) => ({
      ...d,
      sealedProductId: product.id,
      title: product.name,
      coverImageUrl: d.coverImageUrl ?? product.imageUrl,
      templateKey: this.state.pastedSpots.value.length > 0 ? d.templateKey : (template?.key ?? d.templateKey),
      ...this.#moneyFor(product),
    }));
  };

  #pickedProduct(): SealedProduct | null {
    const id = this.state.draft.value.sealedProductId;
    return id ? (this.state.sealedProducts.value.find((p) => p.id === id) ?? null) : null;
  }

  #moneyFor(product: SealedProduct): Pick<BoxDraft, "cost" | "marketValue"> {
    return {
      cost: product.costCents != null ? centsToDollars(product.costCents) : "",
      marketValue: product.marketValueCents != null ? centsToDollars(product.marketValueCents) : "",
    };
  }

  async #loadSealed(): Promise<void> {
    const shopId = this.#shopId;
    if (!shopId) return;
    this.state.sealedStatus.value = loading;
    const r = await loadSealedProducts({ ...this.deps, shopId, filter: "in-stock" });
    if (this.isDisposed) return;
    if (!r.ok) {
      this.state.sealedStatus.value = failedLoad(r.errors);
      return;
    }
    this.state.sealedProducts.value = r.value;
    this.state.sealedStatus.value = loaded;
  }

  pickTemplate = (templateKey: string) => {
    this.#patch({ templateKey });
    this.state.pastedSpots.value = [];
    this.state.parsed.value = null;
    this.state.parse.value = idleForm;
    this.state.pasteOpen.value = false;
  };

  togglePaste = () => {
    this.state.pasteOpen.update((open) => !open);
  };

  uploadList = async (file: File) => {
    // A file is a paste the host did not have to open a spreadsheet for. Same
    // reader, same warnings; Excel users save as CSV first.
    const text = await file.text();
    if (this.isDisposed) return;
    this.state.pasteOpen.value = true;
    this.#patch({ spotText: text });
    await this.readSpots();
  };

  setListName = (listName: string) => {
    this.state.listName.value = listName;
  };

  saveList = async () => {
    const shopId = this.#shopId;
    const labels = this.state.pastedSpots.value.map((s) => s.label);
    const name = this.state.listName.value.trim();
    if (!shopId || labels.length === 0 || name === "" || this.state.saveList.value.busy) return;
    this.state.saveList.value = busyForm;
    const r = await saveSpotList({
      ...this.deps,
      shopId,
      list: { name, category: this.#pickedProduct()?.category ?? "CUSTOM", labels },
    });
    if (this.isDisposed) return;
    if (!r.ok) {
      this.state.saveList.value = failedForm(r.errors);
      return;
    }
    this.state.saveList.value = idleForm;
    this.state.listName.value = "";
    this.state.templates.value = [...this.state.templates.value, r.value];
    // The saved list now stands in for the paste, so the chip streams selected.
    this.state.pastedSpots.value = [];
    this.state.parsed.value = null;
    this.state.pasteOpen.value = false;
    this.#patch({ templateKey: r.value.key });
  };

  removeList = async (spotListId: string) => {
    const shopId = this.#shopId;
    if (!shopId) return;
    const r = await deleteSpotList({ ...this.deps, shopId, spotListId });
    if (this.isDisposed || !r.ok) return;
    this.state.templates.value = this.state.templates.value.filter((t) => t.id !== spotListId);
    if (this.state.draft.value.templateKey === spotListId) this.#patch({ templateKey: null });
  };

  readSpots = async () => {
    const text = this.state.draft.value.spotText;
    if (text.trim() === "" || this.state.parse.value.busy) return;
    this.state.parse.value = busyForm;
    const r = await parseSpots({ ...this.deps, text });
    if (this.isDisposed) return;
    if (!r.ok) {
      this.state.parse.value = failedForm(r.errors);
      return;
    }
    this.state.parse.value = idleForm;
    this.state.parsed.value = r.value;
    if (r.value.spots.length === 0) return;
    this.state.pastedSpots.value = r.value.spots;
    // A custom list is by definition not a league's.
    this.#patch({ templateKey: null });
    this.state.pasteOpen.value = false;
  };

  uploadCover = async (file: File) => {
    if (this.state.cover.value.busy) return;
    this.state.cover.value = busyForm;
    try {
      // `readImageFile` throws on an oversized or unreadable file; the upload's own refusal is the Result.
      const r = await uploadLiveCover({ ...this.deps, image: await readImageFile(file) });
      if (this.isDisposed) return;
      if (!r.ok) {
        this.state.cover.value = failedForm(r.errors);
        return;
      }
      this.#patch({ coverImageUrl: r.value.url });
      this.state.cover.value = idleForm;
    } catch (error) {
      if (this.isDisposed) return;
      this.state.cover.value = failedForm(thrownText(error, "Upload failed."));
    }
  };

  removeCover = () => this.#patch({ coverImageUrl: null });

  create = async () => {
    const shopId = this.deps.storeRepository.shopId;
    const plan = this.state.plan.value;
    if (!shopId || !plan.ready || this.state.create.value.busy) return;
    this.state.create.value = busyForm;
    const r = await addBreaks({ ...this.deps, shopId, breaks: boxRequest(this.state.draft.value, plan) });
    if (this.isDisposed) return;
    if (!r.ok) {
      this.state.create.value = failedForm(r.errors);
      return;
    }
    this.state.create.value = idleForm;
    this.routing.toQueue();
  };

  #patch(patch: Partial<BoxDraft>): void {
    this.state.draft.update((d) => ({ ...d, ...patch }));
  }

  async #loadLists(): Promise<void> {
    const shopId = this.#shopId;
    if (!shopId) return;
    this.state.templatesStatus.value = loading;
    const r = await loadSpotLists({ ...this.deps, shopId });
    if (this.isDisposed) return;
    if (!r.ok) {
      this.state.templatesStatus.value = failedLoad(r.errors);
      return;
    }
    this.state.templates.value = r.value;
    this.state.templatesStatus.value = loaded;
  }
}
